import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import { KeyRound, Copy } from 'lucide-react';

interface IvrApiKey {
  id: string;
  key_prefix: string;
  label: string | null;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

const IvrSettings: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<IvrApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [mintingKey, setMintingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [newKeyLabel, setNewKeyLabel] = useState('');

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoadingKeys(true);
    const { data, error } = await supabase
      .from('tenant_ivr_api_keys')
      .select('id, key_prefix, label, is_active, created_at, last_used_at, revoked_at')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error loading IVR API keys:', error);
    } else {
      setApiKeys((data as IvrApiKey[]) || []);
    }
    setLoadingKeys(false);
  };

  const mintKey = async () => {
    setMintingKey(true);
    setRevealedKey(null);
    const { data, error } = await supabase.functions.invoke('mint-ivr-api-key', {
      body: { label: newKeyLabel.trim() || null },
    });
    if (error || !data?.api_key) {
      toast({ title: 'Error', description: 'Failed to generate key: ' + (error?.message || 'unknown error'), variant: 'destructive' });
    } else {
      setRevealedKey(data.api_key);
      setNewKeyLabel('');
      loadApiKeys();
    }
    setMintingKey(false);
  };

  const revokeKey = async (id: string) => {
    const { error } = await supabase
      .from('tenant_ivr_api_keys')
      .update({ is_active: false, revoked_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      // Frontend has no direct write access to this table (service-role only) -- expected.
      toast({
        title: 'Cannot revoke directly',
        description: 'Ask Claude to revoke this key via the database, or generate a new one and have the IVR developer switch to it.',
        variant: 'destructive',
      });
    } else {
      loadApiKeys();
    }
  };

  const copyKey = () => {
    if (revealedKey) {
      navigator.clipboard.writeText(revealedKey);
      toast({ title: 'Copied', description: 'API key copied to clipboard' });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 border-gray-700 bg-gray-800/30">
        <h3 className="text-lg font-bold text-yellow-500">IVR API Keys</h3>
        <p className="text-sm text-gray-400 mt-1 mb-4">
          Give the IVR developer one of these keys so his system can call our booking endpoints. The full key is shown
          only once, right after you generate it — copy it somewhere safe immediately.
        </p>
        <p className="text-sm text-gray-400 mb-4">
          Caller recognition (fixed location numbers, always-divert-to-operator) now lives on the CRM account itself —
          open a CRM account and look for the "IVR / Caller ID" section, rather than managing it here.
        </p>

        {revealedKey && (
          <div className="mb-4 p-3 border border-yellow-600 bg-yellow-950/30 rounded-lg flex items-center gap-3 flex-wrap">
            <KeyRound className="w-4 h-4 text-yellow-500 shrink-0" />
            <code className="text-sm text-yellow-200 break-all">{revealedKey}</code>
            <Button variant="outline" size="sm" onClick={copyKey}>
              <Copy className="w-3.5 h-3.5 mr-1" />
              Copy
            </Button>
            <span className="text-xs text-yellow-500/80 w-full">
              This won't be shown again — copy it now and send it to the IVR developer securely.
            </span>
          </div>
        )}

        {loadingKeys ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500" />
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {apiKeys.length === 0 && <p className="text-sm text-gray-500">No API keys generated yet.</p>}
            {apiKeys.map((k) => (
              <div key={k.id} className="flex items-center gap-3 flex-wrap border border-gray-700 rounded-lg p-2.5">
                <code className="text-sm text-gray-300">{k.key_prefix}...</code>
                {k.label && <span className="text-sm text-gray-400">{k.label}</span>}
                <span className="text-xs text-gray-500">
                  Created {formatDate(k.created_at)}
                  {k.last_used_at ? ` · last used ${formatDate(k.last_used_at)}` : ' · never used'}
                </span>
                <div className="ml-auto">
                  {k.is_active ? (
                    <Button variant="outline" size="sm" onClick={() => revokeKey(k.id)}>
                      Revoke
                    </Button>
                  ) : (
                    <span className="text-xs text-red-400">Revoked</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-gray-700 pt-4 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs text-gray-400">Label (optional)</Label>
            <Input
              className="w-56"
              value={newKeyLabel}
              onChange={(e) => setNewKeyLabel(e.target.value)}
              placeholder="e.g. IVR production key"
            />
          </div>
          <Button size="sm" onClick={mintKey} disabled={mintingKey}>
            <KeyRound className="w-3.5 h-3.5 mr-1" />
            Generate New Key
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default IvrSettings;
